import React, { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

const FAQSectionEN = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  // FAQ according to user specifications - accordion
  const faqItems = [
    {
      question: "What are the check-in and check-out times?",
      answer: "Check-in is from 4 PM and check-out is before 11 AM. Flexible times can be arranged according to your needs, please don't hesitate to contact us."
    },
    {
      question: "Are bed sheets and towels provided?",
      answer: "Bed sheets are included and beds are made upon your arrival. Towels are not included but available as an option for €6 per person for the whole stay (2 towels per person)."
    },
    {
      question: "Is there private parking on site?",
      answer: "Yes, parking is free and available right next to the gîte. No parking issues even with multiple vehicles."
    },
    {
      question: "Are pets accepted?",
      answer: "Yes, we accept pets at no extra charge. Your four-legged companion will be welcome in our gîte and can enjoy the enclosed garden."
    },
    {
      question: "How does the booking process work?",
      answer: "You can book directly via Airbnb, Booking.com or contact us by email/phone. A 30% deposit is required at booking, with the balance due upon arrival."
    },
    {
      question: "Is the gîte suitable for children?",
      answer: "Absolutely! The gîte has a pétanque court, ping-pong table, hammock and a large secure garden. Baby cot and high chair available on request."
    },
    {
      question: "What activities are nearby?",
      answer: "Clécy offers many activities: hiking, canoeing-kayaking on the Orne, via ferrata, horse riding, fishing, and discovering the landscapes of Suisse Normande. Everything is accessible on foot or within a few minutes by car."
    },
    {
      question: "Is there Wi-Fi and TV?",
      answer: "Yes, high-speed Wi-Fi is free and available throughout the gîte. A television is present in the living room for your relaxation moments."
    },
    {
      question: "What is the cleaning fee? Is it mandatory?",
      answer: "End-of-stay cleaning is mandatory and costs €70. It includes complete cleaning of the gîte, changing of sheets and preparation for the next guests."
    },
    {
      question: "What happens in case of cancellation?",
      answer: "Cancellation conditions vary depending on the booking platform (Airbnb, Booking) or our general conditions for direct bookings. We remain flexible according to circumstances."
    }
  ];

  return (
    <section 
      id="faq" 
      className="section-padding bg-white"
      aria-labelledby="faq-title"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-[#1C3A27]/10 rounded-full flex items-center justify-center">
              <HelpCircle size={32} className="text-[#1C3A27]" />
            </div>
          </div>
          <h2 
            id="faq-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            Practical FAQ
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            All answers to the most frequently asked questions about your stay
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqItems.map((item, index) => {
            const isOpen = openItems.has(index);
            return (
              <div
                key={index}
                className="bg-white rounded-xl border border-[#EDE6DB]/50 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2 rounded-xl"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="font-semibold text-[#1C3A27] text-base md:text-lg pr-4">
                    {item.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {isOpen ? (
                      <ChevronUp size={20} className="text-[#1C3A27]" />
                    ) : (
                      <ChevronDown size={20} className="text-[#1C3A27]" />
                    )}
                  </div>
                </button>
                
                <div
                  id={`faq-answer-${index}`}
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 pb-5">
                    <div className="border-t border-[#EDE6DB]/30 pt-4">
                      <p className="text-[#1C3A27]/80 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact for additional questions */}
        <div className="mt-12 md:mt-16 text-center">
          <div className="bg-[#EDE6DB]/30 rounded-2xl p-6 md:p-8">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#1C3A27] mb-4">
              Another question?
            </h3>
            <p className="text-[#1C3A27]/70 mb-6 leading-relaxed">
              Feel free to contact us directly. Nathalie & Wilhelm will be happy to help you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:wilhelm.martel@orange.fr"
                className="btn-primary px-6 py-3 hover:bg-[#2A5236] transition-colors"
                aria-label="Contact us by email"
              >
                ✉️ Email us
              </a>
              <a
                href="tel:+33664104815"
                className="btn-secondary px-6 py-3 hover:bg-[#1C3A27] hover:text-white transition-colors"
                aria-label="Call us"
              >
                📞 Call us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSectionEN;